import React from "react";
import { ExternalLink } from "lucide-react";
import { EXTERNAL_LINKS } from "../../../routes/paths";

/**
 * The documents the account is held to, and where each is published.
 *
 * The first three are the ones the sign-up consent screen asks agreement to —
 * see `CONSENT_ROWS` — and the refund policy is the promise the price list
 * makes, whose dates come off `GET /api/v1/billing/refund/`. All are linked rather
 * than reproduced: the published text is the one that was agreed to, and a copy
 * pasted into this app would be a second version of a legal document with
 * nothing keeping it in step.
 */
const DOCUMENTS = [
  {
    title: "Privacy policy",
    description:
      "What personal data Naurist holds, why it is held, and how long for.",
    href: EXTERNAL_LINKS.privacyPolicy,
  },
  {
    title: "Terms of use",
    description: "The agreement between your practice and Naurist.",
    href: EXTERNAL_LINKS.termsOfUse,
  },
  {
    title: "Data processing agreement",
    description:
      "How your clients' data is processed on your behalf, as the DPDP Act requires.",
    href: EXTERNAL_LINKS.dataProcessingAgreement,
  },
  // {
  //   title: "Refund policy",
  //   description:
  //     "When a subscription can be refunded, and how the money comes back.",
  //   href: EXTERNAL_LINKS.refundPolicy,
  // },
];

/**
 * When the published documents last changed, as they all changed together.
 *
 * Written here by hand, and so it has to be edited here when a document is
 * republished. The app is not told about a change on the marketing site, and a
 * date that quietly went stale would be worse than none: it would say the terms
 * had not moved when they had.
 */
const LAST_UPDATED = { iso: "2026-08-01", label: "1 August 2026" };

/** Settings → Policy: the paperwork, each document where it is published. */
const PolicyTab: React.FC = () => (
  <div className="flex flex-col gap-4">
    {/* Ahead of the list, since it is true of every document below it. */}
    <p className="text-[13px] 3xl:text-[14px] text-ink-muted">
      Last updated <time dateTime={LAST_UPDATED.iso}>{LAST_UPDATED.label}</time>
    </p>

    {DOCUMENTS.map((document) => (
      <section
        key={document.title}
        className="card-surface border border-line bg-white"
      >
        {/* Held on one line on a phone — the document and what it is on the
            left, the way to it against the right edge — which is how a phone
            lists things there is one thing to do with. Allowed to wrap again
            from `md` up, where the design has it and where a long description
            has the room to want a second line. The `Read` never wraps either
            way: it is `flex-shrink-0`, and the column beside it is `min-w-0`,
            so the description shortens instead of pushing it off the card. */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 md:flex-wrap 3xl:px-6 3xl:py-5">
          <div className="min-w-0">
            <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
              {document.title}
            </h2>
            <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
              {document.description}
            </p>
          </div>

          {/* A link and not a button: it goes somewhere, and it opens on the
              marketing site rather than in here — which is what the new tab
              and the icon beside it say before it is pressed. */}
          <a
            href={document.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-shrink-0 items-center gap-1.5 text-[14px] 3xl:text-[15px] font-medium text-brand-dark hover:underline"
          >
            Read
            <ExternalLink aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </div>
      </section>
    ))}
  </div>
);

export default PolicyTab;
